import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { tx } from '../i18n';
import { PUERTA } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { hace } from '../lib/formato';
import type { ApplianceDetalle } from '../tipos';
import { Consulta, Pastilla, Vacio } from '../ui/piezas';

/**
 * La app del cliente, adentro del Centro (decisión 19 §7.3). No es una copia ni una pestaña
 * aparte: es su KNOC360 tal cual, servido por el canal, dentro del marco del panel — así
 * quien atiende no pierde de vista en qué cliente está ni con qué permiso entró.
 */
/**
 * Dónde estaba quien atiende adentro de la app del cliente. La app embebida lo cuenta por
 * `postMessage` y acá queda en el hash de la dirección, así recargar la ventana vuelve al
 * mismo lugar en vez de al tablero. Solo se guarda una ruta: nada de lo que se ve adentro.
 */
function useRutaDeAdentro(): string {
  const [inicial] = useState(() => decodeURIComponent(window.location.hash.slice(1)));
  useEffect(() => {
    const oir = (e: MessageEvent) => {
      const d = e.data as { knoc360?: string; ruta?: string } | null;
      if (!d || d.knoc360 !== 'ruta' || typeof d.ruta !== 'string' || !d.ruta.startsWith('/')) return;
      // replaceState y no navegación: no se llena el historial con cada clic de adentro
      window.history.replaceState(null, '', `${window.location.pathname}#${encodeURIComponent(d.ruta)}`);
    };
    window.addEventListener('message', oir);
    return () => window.removeEventListener('message', oir);
  }, []);
  return inicial;
}

export function RedDeCliente() {
  const { id = '' } = useParams();
  const rutaDeAdentro = useRutaDeAdentro();
  const detalle = useConsulta<ApplianceDetalle>(`/appliances/${id}`, { refetchInterval: 60_000 });
  const red = useConsulta<{ nivel: string; hora: string | null }>(`/red/${id}`);
  const a = detalle.data;
  const nivel = red.data?.nivel ?? 'ninguno';

  return (
    <div className="dentro">
      <header className="dentro-barra">
        <Link to={`/appliances/${id}`} className="dentro-volver">
          {tx.red.volverAlAppliance}
        </Link>
        <b>{a?.organizacion ?? '…'}</b>
        <span className="d">{a?.nombre}</span>
        {nivel !== 'ninguno' && (
          <Pastilla tono={nivel === 'operar' ? 'parcial' : 'desconocido'}>{nivel === 'operar' ? tx.red.verYOperar : tx.red.soloVer}</Pastilla>
        )}
        {a && !a.en_linea && <Pastilla tono="caido">{tx.red.sinContacto}</Pastilla>}
        <span className="dentro-nota d">
          {tx.red.estasAdentro}
          {a?.visto ? tx.red.ultimaVez(hace(a.visto)) : ''}
        </span>
      </header>
      <Consulta q={red} vacio={<Vacio>{tx.red.noDejaVer}</Vacio>}>
        {nivel === 'ninguno' ? (
          <Vacio>{tx.red.noDejaVer}</Vacio>
        ) : !a?.en_linea ? (
          <Vacio>{tx.red.sinContactoAhora}</Vacio>
        ) : (
          <iframe
            className="dentro-marco"
            title={tx.red.marco(a?.organizacion ?? tx.red.cliente_)}
            src={`${PUERTA}/abrir/${id}${rutaDeAdentro ? `?ir=${encodeURIComponent(rutaDeAdentro)}` : ''}`}
          />
        )}
      </Consulta>
    </div>
  );
}
